<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-configuration-topic-blacklist" v-loading="isExportLoading">
    <div class="export-wrapper">
      <el-button class="export-btn" @click="handleExportBlacklist" :disabled="!groupTopics.length">
        <div class="export-icon"></div>
        <span class="export-text">Export</span> 
      </el-button>
    </div>
    <div class="page-wrapper">
      <div class="topic-group-section">
        <div class="div-group-viewer-header div-group-name">
          <div>Conversation Topics Blacklist</div>
          <el-tooltip popper-class="wy-page-tooltip-popup-configuration" content="Full list of conversation topics blacklisted since OC was setup" effect="dark" placement="bottom">
            <img class="info-icon" src="../../../assets/icons/icon_info.svg" />
          </el-tooltip>
        </div>
        <div class="div-group-viewer-content">
          <div class="div-group-topics" v-bind:class="{ 'isDisabled': loadingMsg }">
            <div class="div-topic-content" ref="topicContent" v-loading="loading">
              <div v-if="groupTopics && groupTopics.length > 0">
                <div class="div-item" :title="topic.topicDisplayName" v-for="(topic) in groupTopics" :label="topic"
                  :key="topic.Name" @click="getMessagesByTopics(topic, 1, $event)">
                  <div class="div-ellipsis-item">{{ topic.topicDisplayName }}</div>
                  <!-- <div class="div-ellipsis-item" @mouseover="mouseover($event, topic.topicDisplayName, null, null)" @mouseout="mouseout">{{ topic.topicDisplayName }}</div> -->
                  <i v-if="isRoleAllow([4201, 900011])" @mouseover="mouseover($event, 'Unblacklist', null, 10)" @mouseout="mouseout" class="el-icon-error" @click="removeTopic(topic, $event)"></i>
                </div>
              </div>
              <div v-else class="no-data">
                <div class="no-data-content">
                  There are no blacklisted conversation topics. Topics can be blacklisted through the configuration or the
                  conversation topics page.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <div v-if="showMessageContent" class="topic-messages-section">
        <div class="div-topic-messages-viewer-header">
          <span>Messages</span>
          <el-tooltip popper-class="wy-page-tooltip-popup-configuration" content="Messages within blacklisted topic recorded in the last 6 months only" effect="dark" placement="bottom">
            <img class="info-icon" src="../../../assets/icons/icon_info.svg" />
          </el-tooltip>
        </div>
        <div class="div-topic-messages-viewer-content" v-loading="loadingMsg">
          <div class="div-group-viewer-content">
            <div class="div-group-topics" ref="messageContent">
              <div v-if="topicMessages && topicMessages.length" class="div-topic-content">
                <div class="div-item" :title="item.messageText" v-for="(item, index) in topicMessages" :key="index"
                  v-bind:class="{ selected: (selectedMessageText && selectedMessageText == item.messageText) }"
                  @click="getTranscriptByMessageText(item)">
                  <div class="div-ellipsis-item">{{ item.messageText }}</div>
                  <!-- <div class="div-ellipsis-item" @mouseover="mouseover($event, item.messageText, null, null)" @mouseout="mouseout">{{ item.messageText }}</div> -->
                  <div class="div-messages-counter">({{ item.counter }})</div>
                </div>
              </div>
              <div v-else class="div-no-topic">
                No messages available.
              </div>
            </div>
          </div>
        </div>
        <div v-if="topicMessages && topicMessages.length" class="div-group-viewer-footer div-group-name">
          <div class="div-message-transcripts-viewer-footer">
            <div class="div-transcript-page-infor">Showing {{ pageItemStart }}-{{ pageItemEnd }}</div>
            <div class="div-transcript-pagination">
              <el-pagination :page-size="pageSize" :page-count="pageCount" :current-page="pageNo" :disabled="loading" prev-text="< Previous"
                next-text="Next >" @current-change="pageChanged" layout="prev, next">
              </el-pagination>
            </div>
          </div>
        </div>
      </div>
  
      <wy-analytics-transcript-viewer ref="transcriptViewer" v-show="showMessageContent" :controller="module" :showTranscriptContent="showTranscriptContent" :topics="selectedTopic.mergedTopics" 
        :messageText="selectedMessageText" :fromDate="fromDate" :applyGlobalFilter="false" viewName="Messages" />
    </div>
    <!-- un-blacklist popup -->
    <wy-dialog 
      ref="unBlacklistDialog"
      cancelButtonLabel="Cancel"
      confirmButtonLabel="Unblacklist"
      :controller="module">
    </wy-dialog>
    </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import WyUtils from '../../../library/wy-utils.js';
import WyDialog from '../../widgets/wy-dialog'
import WyAnalyticsTranscriptViewer from '../wy-analytics-transcript-viewer2.vue';
import moment from 'moment';

export default Vue.extend({
  name: 'wy-analytics-configuration-topic-blacklist',
  title: 'Configuration',
  components: {
    'wy-dialog': WyDialog,
    'wy-analytics-transcript-viewer': WyAnalyticsTranscriptViewer
  },
  props: ['controller'],
  
  data() {
    return {
      module: this,
      loading: false,
      loadingMsg: false,
      isExportLoading: false,
      groupTopics: [],
      topicMessages: [],
      topicMessage: null,
      messageTranscripts: [],

      showMessageContent: false,
      showTranscriptContent: false,
      preMsgObj: null,

      selectedTopic: {},
      selectedMessageText: null,
      fromDate: null,
      pageNo: 1,
      pageSize: 100,
      pageCount: 1,
      pageItemStart: 0,
      pageItemEnd: 0,
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'isRoleAllow', 'transcriptInfoReference']),
  },

  methods: {
    ...mapActions(['fetchContactReasonsBlackListsTopics', 'fetchContactReasonsBlackListsTopicMessages', 'updateContactReasonTopicBlackList', 'exportBlacklistedTopicMessages']),
    ...mapMutations(['updateTranscriptInfoReference']), //handle transcript info

    handleTranscriptPopover() {
      if (this.transcriptInfoReference) {
        this.transcriptInfoReference.visible = false;
        this.updateTranscriptInfoReference(null);
      }
    },

    async handleExportBlacklist() {
      this.isExportLoading = true;
      await this.exportBlacklistedTopicMessages({ dateStart: this.fromDate })
      this.isExportLoading = false;
    },

    removeTopic(topic, event) {
      // this.$refs.unBlacklistDialog.title = `Delete alerts for "${this.getKPILabel(kpi.metric)}" ?`;
      this.$refs.unBlacklistDialog.title = `Are you sure you want to unblacklist this topic?`;
      this.$refs.unBlacklistDialog.content="Once unblacklisted, the topic will appear as a Conversation Topic and would be included in the analysis involving conversation topics.  " 
      this.$refs.unBlacklistDialog.open();
      this.$refs.unBlacklistDialog.confirmButtonCallback = async () => {
        this.handleRemoveTopic(topic, event) 
      }
    },

    async handleRemoveTopic(topic, event) {
      event.preventDefault();
      event.stopPropagation();

      if (topic.mergedTopics && topic.mergedTopics.length > 0) {
        this.loading = true;
        this.loadingMsg = true;
        this.showTranscriptContent = false;

        let res = await this.updateContactReasonTopicBlackList(topic);
        this.loading = false;
        this.loadingMsg = false;
        this.showTranscriptContent = true;

        if (!res) {
          this.$root.showNotificationLocale("error", "error.serverErrorTitle", "error.serverErrorMsg");
        }
        else if (res.status == 200) {
          this.$root.showProcessWaitDialogue({ visible: true, processId: res.data.processId });
          setTimeout(async () => {
            let index = this.groupTopics.indexOf(topic);
            if (index > -1) {
              this.groupTopics.splice(index, 1);
            }
          }, 1000)

          this.selectFirstTopic();
        }
        else if (res.status == 409) {
          this.$root.showProcessErrorDialogue({ controller: this, info: res.data });
        }
      }
    },

    async loadBlackList() {
      this.showMessageContent = false;
      this.showTranscriptContent = false;
      this.loading = true;

      let result = await this.fetchContactReasonsBlackListsTopics();
      if (result && result.data && result.data.length > 0) {
        this.groupTopics = result.data;
        if (this.groupTopics && this.groupTopics.length > 0) {
          this.selectFirstTopic();
        }
        else {
          this.showMessageContent = false;
          this.showTranscriptContent = false;
        }
      }
      this.loading = false;
    },

    selectFirstTopic() {
      this.getMessagesByTopics(this.groupTopics[0], 1);
      this.$nextTick(() => {
        this.$refs.topicContent.querySelectorAll(".div-item").forEach(el => {
          el.classList.remove("selected");
        })
        this.$refs.topicContent.querySelector(".div-topic-content .div-item").classList.add("selected");
      })
    },

    handleTimeInNiceFormat(Millis) {
      let seconds = Millis / 1000;

      return WyUtils.toNiceTimeFormat(seconds);
    },

    pageChanged(pageNo) {
      this.fetchData = true;
      this.getMessagesByTopics(this.selectedTopic, pageNo);
    },

    async getMessagesByTopics(topic, pageNo, event) {
      // let topics = topic.mergedTopics;
      this.selectedTopic = topic;
      // this.topicMessages = [];
      this.loadingMsg = true;
      this.showMessageContent = true;
      this.showTranscriptContent = false;
      this.pageNo = pageNo;

      if (event) {
        if (event.target.classList.contains("div-ellipsis-item")) {
          this.removeClassNameFromIcon(event, "selected");
          event.target.parentNode.classList.add("selected");
        }
        else {
          this.removeClassName(event, "selected");
          event.target.classList.add("selected");

          if (event.target.classList.contains("el-icon-error")) {
            this.$refs.topicContent.querySelectorAll(".div-item").forEach(el => {
              el.classList.remove("selected");
            })
            event.target.parentNode.classList.add("selected");
          }
        }
      }
      else {
        if(this.$refs.topicContent.querySelectorAll(".div-item") && this.$refs.topicContent.querySelectorAll(".div-item").length > 0) {
          this.$refs.topicContent.querySelectorAll(".div-item")[0].classList.add("selected")
        }
      }

      let result = await this.fetchContactReasonsBlackListsTopicMessages({ dateStart: this.fromDate, topics: topic.mergedTopics, pageNo: this.pageNo, pageSize: this.pageSize + 1 });

      if (result && result.data && result.data.messages && result.data.messages.length > 0) {
        if (result.data.messages.length > this.pageSize) {
          this.topicMessages = result.data.messages.slice(0, result.data.messages.length - 1);
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + this.pageSize - 1;
          this.pageCount = this.pageNo + 1;
        }
        else {
          this.topicMessages = result.data.messages;
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + result.data.messages.length - 1;
          this.pageCount = this.pageNo;
        }

        await this.getTranscriptByMessageText(this.topicMessages[0]);
        this.$refs.messageContent.querySelector(".div-group-topics .div-item").classList.add("selected");

        //this.topicMessages = result.data.messages;
        //this.messageTranscripts = this.topicMessages[0].messages;
        //this.topicMessage = this.topicMessages[0];
        // Below is commented out to follow the same convention as other components (line 33 v-bind:class)
        // setTimeout(()=>{
        //   if(document.querySelector(".wy-analytics-configuration-topic-blacklist .div-topic-message-name")) {
        //     document.querySelector(".wy-analytics-configuration-topic-blacklist .div-topic-message-name").classList.add("selected");
        //   }
        // }, 100)
      }
      else {
        this.topicMessages = []
        this.messageTranscripts = []
        this.showTranscriptContent = false;

        this.messages = [];
        this.pageNo = 1;
        this.pageCount = 0;
        this.pageItemStart = 0;
        this.pageItemEnd = 0;
      }

      this.loadingMsg = false;
    },

    async getTranscriptByMessageText(item) {
      this.selectedMessageText = item.messageText;
      this.showTranscriptContent = true;
    },

    formatMessageHeaderDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    formatMessageDate(date) {
      return WyUtils.formatDate(date);
    },

    getTimeRange(transcript) {
      if(transcript.msgTime) {
        return transcript.msgTime
      }
      else {
        // TODO: This is for V2 API and can be removed later 
        return WyUtils.getTimeRange(transcript);
      }
    },

    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },

    addClassName(e) {
      let domEl = null;
      if (e.target.classList.contains("div-topic-message-name")) {
        domEl = e.target;
      }
      if (e.target.parentNode.classList.contains("div-topic-message-name")) {
        domEl = e.target.parentNode;
      }
      if (e.target.parentNode.parentNode.classList.contains("div-topic-message-name")) {
        domEl = e.target.parentNode.parentNode;
      }
      domEl.classList.add("selected");
    },

    removeClassName(e, className) {
      let domEl = null;
      if (e.target.classList.contains("div-topic-message-name")) {
        domEl = e.target;
      }
      if (e.target.parentNode.classList.contains("div-topic-message-name")) {
        domEl = e.target.parentNode;
      }
      if (e.target.parentNode.parentNode.classList.contains("div-topic-message-name")) {
        domEl = e.target.parentNode.parentNode;
      }
      if (domEl && domEl.classList.length > 0) {
        let removeClassName = domEl.classList[0];
        document.querySelectorAll(".wy-analytics-configuration-topic-blacklist ." + removeClassName).forEach(el => {
          if ((' ' + el.className + ' ').indexOf(' ' + className + ' ') > -1) {
            el.classList.remove(className);
          }
        })
      }
      else {
        let removeClassName = e.target.classList[0];
        document.querySelectorAll(".wy-analytics-configuration-topic-blacklist ." + removeClassName).forEach(el => {
          if ((' ' + el.className + ' ').indexOf(' ' + className + ' ') > -1) {
            el.classList.remove(className);
          }
        })
      }
    },

    removeClassNameFromIcon(e, className) {
      if (e.target.parentNode.classList.length > 0) {
        let removeClassName = e.target.parentNode.classList[0];
        document.querySelectorAll(".wy-analytics-configuration-topic-blacklist ." + removeClassName).forEach(el => {
          if ((' ' + el.className + ' ').indexOf(' ' + className + ' ') > -1) {
            el.classList.remove(className);
          }
        })
      }
    },

    mouseover(e, tooltips, type, scaleX) {
      e.stopPropagation();
      if(tooltips && tooltips.trim() != '') {
        this.mouseoverHandler = e;
        let tooltipsEl = document.querySelector(".site-tooltips");
        if(tooltipsEl) {
          tooltipsEl.innerHTML = tooltips
          tooltipsEl.style.display = 'block';
          tooltipsEl.style.left = `${e.target.getBoundingClientRect().x + 10}px`;
          let scaleXPosition = 0
          if(!scaleX) {
            scaleXPosition = tooltipsEl.getBoundingClientRect().width/6
          }
          else {
            scaleXPosition = tooltipsEl.getBoundingClientRect().width/2 - scaleX
          }
          if(type == "right") {
            tooltipsEl.style.right = "10px";
            tooltipsEl.style.left = "unset";
          }
          if(type == "left") {
            tooltipsEl.style.left = "10px";
            tooltipsEl.style.right = "unset";
          }
          if(!type || type == "normal") {
            tooltipsEl.style.left = `${e.target.getBoundingClientRect().x - scaleXPosition}px`;
          }
          tooltipsEl.style.top = `${e.target.getBoundingClientRect().top + e.target.getBoundingClientRect().height + 5}px`;
        }
      }
      else {
        e.preventDefault();
      }
    },
    mouseout(e) {
      if(e) {
        e.stopPropagation();
      }
      this.mouseoverHandler = null;
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl) {
        tooltipsEl.style.display = 'none';
      }
    },
  },

  mounted() {
    this.loadBlackList();
      // limit to last 6 months data
    this.fromDate = moment().subtract(6, 'months').format('YYYY-MM-DD');

    // on scroll, we close the transcript popover & any opened dropdowns
    document.querySelector('.wy-analytics-configuration .el-tabs__content').addEventListener('scroll', () => {
      this.handleTranscriptPopover();
    });
  }
});
</script>

<style lang="scss">
$panel-width: 330px;

.wy-page-tooltip-popup-configuration {
  max-width: 250px;
}
.contact-reason-group-topics-tab-info-pop-over {
  border-color: var(--site-font-chart-color) !important;
  color: #E4E8EC;
  line-height: 18px;

  .popper__arrow:after,
  .popper__arrow:before {
    display: block !important;
    border-bottom-color: #000 !important;
  }

  .popper-item {
    margin-bottom: 15px;
  }

}

.wy-analytics-configuration-topic-blacklist {
  height: 100%;

  .page-wrapper {
    display: inline-flex;
    width: 100%;
    height: calc(100% - 64px);
    min-width: 1350px;
  }

  .export-wrapper {
    display: flex;
    width: 100%;
    max-width: 1385px;
    min-width: 1289px;
    padding-bottom: 18px;
    justify-content: flex-end;

    .export-btn {
      padding: 6px 15px 9px 11px !important;
      border-radius: 5px;
      background: var(--site-button-secondary-btn-background-primary-color);
      color: var(--site-button-secondary-btn-text-primary-color) !important;
      border: 1px solid var(--site-button-secondary-btn-hover-border-primary-color);
      font-size: 16px !important;
      display: flex;
      align-items: center;

      .btn-wrapper {
        display: flex ;
        align-items: center;
      }

      // .export-arrow {
      //   background-image: url('../../../assets/icons/icon_intent_export.svg');
      //   background-repeat: no-repeat;
      //   height: 20px;
      //   width: 20px;
      //   background-position-y: 2px;
      // }
    }
    .export-btn:hover {
      border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
      background-color: var(--site-button-secondary-btn-hover-background-primary-color) !important;
      border-radius: 5px;
      color: var(--site-button-secondary-btn-text-primary-color) !important;
    }
    .export-btn:active {
      background-color: var(--site-button-secondary-btn-pressed-background-primary-color) !important;
    }
    .export-btn:focus {
      border: 1px var(--site-button-secondary-btn-hover-border-primary-color) solid !important;
      border-radius: 5px;
      color: var(--site-button-secondary-btn-text-primary-color) !important;
    }
    .export-icon {
      background: var(--icon_action_export);
      height: 17px;
      width: 13px;
      position: relative;
      top: 2px;
      height: 17px;
      width: 13px;
      display: inline-block;
      background-repeat: no-repeat;
    }
    .export-text {
      position: relative;
      bottom: 1px;
      left: 5px;
    }
  }

  .transcripts-viewer-header-right {
    overflow-y: auto;
    height: 204px !important;
    padding-right: 20px;

    &::-webkit-scrollbar {
      background-color: #000 !important;
      width: 8px !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #C8C8C8 !important;
      border-radius: 5px;
    }
  }

  .contact-reason-transcript-tab-info-pop-over {
    padding: 12px 7px 10px 15px !important;
  }

  .popper__arrow:after,
  .popper__arrow:before {
    display: block !important;
    border-bottom-color: #000 !important;
  }

  .sentiment-icon-positive {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_positive_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    right: 61px;
    bottom: 8px;
    border-radius: 100px;
    border: 0px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }

  .sentiment-icon-neutral {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_neutral_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    bottom: 8px;
    right: 61px;
    border-radius: 100px;
    border: 0px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }

  .sentiment-icon-negative {
    cursor: pointer;
    background-image: url('../../../assets/icons/icon_negative_senti.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 5px;
    position: relative;
    right: 61px;
    border-radius: 100px;
    border: 0px;
    bottom: 8px;
    margin: 6px 13px 0 0;
    padding: 12px 10px 14px 14px;
    float: right;
  }

  .isDisabled {
    pointer-events: none;
    opacity: 0.4;
  }

  .info-icon {
    margin-top: 2px;
    width: 20px;
    height: 20px;
  }

  .topic-group-section {
    // min-width: 330px;
    // max-width: 345px;
    // min-width: $panel-width;
    margin-right: 25px;
    width: 330px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #CAD2DA;
    border-radius: 15px;
    height: 100%;
    
    .div-group-viewer-content {
      border-top: 1px solid #CAD2DA;
      overflow-y: auto;
      overflow-x: hidden;
      height: calc(100% - 56px);
      border-bottom-left-radius: 13px;

      &::-webkit-scrollbar {
          width: 14px;
          border-left: 1px solid #E8E8E8;
          background-color: #FAFAFA;
          border-bottom-right-radius: 15px;
        }
      &::-webkit-scrollbar-thumb {
        border-radius: 160px;
        border: 3px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }

    }

    .div-group-viewer-header.div-group-name {
      color: var(--site-font-secondary-color);
      font-size: 16px;
      font-weight: 500;
      padding: 16px 0px 16px 23px;

      display: flex;
      align-items: center;
      gap: 8px;
    }

    .div-group-topics {
      margin-bottom: 8px;

      .div-topic-content {
        .div-item {
          color: var(--site-font-primary-color);
          font-weight: normal;
          margin: 0px 0px 0px 0px;
          line-height: 19px;
          padding: 12px 10px 5px 23px;
          font-size: 12px;
          cursor: pointer;

          .div-ellipsis-item {
            // width: 88%;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 258px;
          }
        }

        .div-item:hover {
          background: #f5f7fa;
        }

        .div-item.selected {
          background: #E2ECF7;
        }

        .el-icon-error {
          float: right;
          left: -5px;
          position: relative;
          color: #1A55AD;
          cursor: pointer;
          font-size: 17px;
        }

        .el-icon-error.is-disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .div-item:hover .el-icon-error {
          display: block;
        }
      }
    }
  }
  .div-no-topic {
    padding: 16px 0px 0px 23px;
    font-size: 12px;
    color: var(--site-font-primary-color);
  }

  .topic-messages-section {
    min-width: $panel-width;
    width: 335px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #CAD2DA;
    border-radius: 15px 0px 0px 15px;
    opacity: 1;
    height: 100%;

    .div-topic-messages-viewer-header {
      color: var(--site-font-secondary-color);
      font-size: 16px;
      font-weight: 500;
      padding: 12px 0px 18px 23px;

      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;
    }

    .div-topic-messages-viewer-content {
      border-top: 1px solid #CAD2DA;
      border-bottom: 1px solid #CAD2DA;
      height: calc(100% - 92px);
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        width: 14px;
        border-left: 1px solid #E8E8E8;
        background-color: #FAFAFA;
        border-bottom-right-radius: 15px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 160px;
        border: 3px solid transparent;
        background-clip: content-box;
        background-color: #CAD2DA;
      }

      .div-group-viewer-content .div-group-topics .div-topic-content {
        width: 320px; 
        .div-item {
          font-size: 12px;
          word-break: keep-all;
          cursor: pointer;
          color: var(--site-font-primary-color);
          font-weight: normal;
          margin: 0px 0px 0px 0px;
          line-height: 20px;
          padding: 12px 10px 10px 23px;
          display: flex;
          justify-content: space-between;

          .div-messages-counter {
            margin-right: 8px;
          }
  
          &:hover {
            background: #f5f7fa;
          }
  
          &.selected {
            background: #E2ECF7;
          }
          .div-ellipsis-item {
            // width: 80%;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

    }
    .div-group-viewer-footer {
      font-size: 12px;
      background-color: white;
      border-bottom-left-radius: 14px;
      width: 334px;

      .div-message-transcripts-viewer-footer {
        padding: 2px 14px 0px 14px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .div-transcript-page-infor {
          font-weight: 500;
          color: #ADADAD
        }

        .div-transcript-pagination {
          .btn-prev, .btn-next {
            font-weight: bold;
          }
        }
      }
    }
  }

  .el-pagination button.btn-prev {
    color: #002852;
  }
  .el-pagination button.btn-next {
    color: #002852;
  }
  .el-pagination button:disabled {
    color: #C0C4CC;
  }

  .el-message {
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    border-width: 1px;
    border-style: solid;
    border-color: #FFF;
    position: fixed;
    left: 50%;
    top: 90px;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    background-color: #395167;
    -webkit-transition: opacity .3s, top .4s, -webkit-transform .4s;
    transition: opacity .3s, top .4s, -webkit-transform .4s;
    transition: opacity .3s, transform .4s, top .4s;
    transition: opacity .3s, transform .4s, top .4s, -webkit-transform .4s;
    padding: 15px 15px 15px 20px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    // background: #FCFCFC 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid #214586;
    border-radius: 15px;
    width: fit-content !important;
    min-width: unset;
  }

  .el-message .el-icon-warning {
    color: var(--site-font-primary-color) !important;
  }

  .el-message__icon {
    margin-right: 10px;
  }

  .el-icon-warning:before {
    content: "\E7A3";
  }

  .el-message--warning .el-message__content {
    color: #FFF !important;
    padding-right: 30px;
  }

  .undo-merge-success-message .el-message__content {
    padding-right: 0px;
  }

  .el-loading-mask {
    background-color: rgba(255, 255, 255, 1) !important;
    .el-loading-spinner {
      top: 35%;
      background: var(--site-loading-icon);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: center;

      .circular {
        display: inline-block;
        width: 90px;
        height: 90px;
      }
    }

    .el-loading-spinner .path {
      -webkit-animation: loading-dash 2s ease-in-out infinite;
      animation: loading-dash 2s ease-in-out infinite;
      stroke-dasharray: 90, 150;
      stroke-dashoffset: 0;
      stroke-width: 2.5 !important;
      stroke: #0C2837 !important;
      stroke-linecap: round;

    }
  }

  .el-message.undo-merge-message p {
    padding-right: 30px !important;
  }

  .el-message p {
    padding-right: 4px !important;
  }

  .el-message__content {
    padding: 0;
    font-size: 14px;
    line-height: 1;
  }

  .no-data {
    padding: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .no-data-icon {
      display: block;
      border: none;
      height: 155px;
      width: 155px;
      background-position-x: 50%;
      background-repeat: no-repeat;
      background-image: url(../../../assets/icons/icon_no_data_new.svg);
      margin-bottom: 10px;
    }

    .no-data-title {
      font-size: 20px !important;
      color: var(--site-font-tertiary-color) !important;
      font-weight: 600;
      margin-bottom: 12px;
    }

    .no-data-content {
      font-size: 14.22px;
      color: var(--site-font-tertiary-color) !important;
      line-height: 18px;
      font-weight: 400;
      text-align: left;
    }
  }

  .message-transcripts-section {
    padding: 0px 0px 0px 0px;
    border: 1px solid #CAD2DA;
    border-left: none;
    height: calc(100% - 0px);
    width: 677px;
    
    .div-message-transcripts-viewer-header {
      .header {
        bottom: 0px;
      }
      min-width: 600px;
      // padding: 6px 12px 2px 12px;
      padding: 18px 12px 9px 12px;
      border-bottom: 1px solid #CAD2DA;
    }
    
    .div-message-transcripts-viewer-content {
      height: calc(100% - 93px);
      padding-bottom: 0px;
    }
    .div-message-transcripts-viewer-footer {
      padding: 0px 14px;
    }
  }

  .wy-dialog .popup .popup-confirm-btn {
    width: 105px;
  }

  .div-message-transcripts-viewer-header {
    .right-section {
      bottom: 6px !important;
      .saved-transcript-golden-star, .saved-transcript-grey-star {
        margin-top: 6px !important;
      }
    }
  }
}
</style>